import axios from 'axios';

import { createErr, createOk, Result } from 'types/result';
import {
  OriginalCustomer,
  OriginalNewCustomer,
  isOriginalNewCustomer,
  isOriginalCustomers,
  NewCustomer,
  OriginalCustomerDetail,
  isOriginalCustomerDetail,
  isOriginalCustomer,
  UpdateCustomer,
} from 'types/customer';
import { isOriginalZeusTokens, OriginalZeusToken } from 'types/zeusToken';
import {
  isOriginalContracts,
  NewContract,
  OriginalContract,
  UpdateContract,
} from 'types/contract';
import { constructUrl } from './utils';

const URL = '/v1/customers';

/**
 * 顧客一覧を取得する
 * @returns 顧客一覧
 */
export const fetchCustomers = async (): Promise<
  Result<OriginalCustomer[], Error>
> => {
  const url = constructUrl(URL);

  return axios
    .get(url)
    .then((res) => {
      const data = res.data as unknown;

      if (!isOriginalCustomers(data)) {
        throw Error(`Unsupported Value: ${JSON.stringify(data)}`);
      }

      return createOk(data);
    })
    .catch((err: Error) => createErr(err));
};

/**
 * 顧客を取得する
 * @param id 顧客 ID
 * @returns 顧客
 */
export const fetchCustomer = async (
  id: number,
): Promise<Result<OriginalCustomerDetail, Error>> => {
  const url = constructUrl(`${URL}/${id}`);

  return axios
    .get(url)
    .then((res) => {
      const data = res.data as unknown;
      if (!isOriginalCustomerDetail(data)) {
        throw Error(`Unsupported Value: ${JSON.stringify(data)}`);
      }

      return createOk(data);
    })
    .catch((err: Error) => createErr(err));
};

/**
 * 顧客に紐づく Zeus トークンを取得する
 * @param id 顧客 ID
 * @returns 顧客
 */
export const fetchZeusToken = async (
  id: number,
): Promise<Result<OriginalZeusToken, Error>> => {
  const url = constructUrl(`${URL}/${id}/tokens`);

  return axios
    .get(url)
    .then((res) => {
      const data = res.data as unknown;
      if (!isOriginalZeusTokens(data)) {
        throw Error(`Unsupported Value: ${JSON.stringify(data)}`);
      }

      // 配列で渡ってくるが要素は必ず一つの想定
      return createOk(data[0]);
    })
    .catch((err: Error) => createErr(err));
};

/**
 * 顧客情報を作成する
 * @param params 顧客情報作成に必要なパラメータ
 * @returns 作成された顧客情報
 */
export const postCustomer = async (
  params: NewCustomer,
): Promise<Result<OriginalNewCustomer, Error>> => {
  const url = constructUrl(URL);

  return axios
    .post(url, {
      ...params,
    })
    .then((res) => {
      const data = res.data as unknown;

      if (!isOriginalNewCustomer(data)) {
        throw Error(`Unsupported Value: ${JSON.stringify(data)}`);
      }

      return createOk(data);
    })
    .catch((err: Error) => createErr(err));
};

/**
 * 顧客情報を更新する
 * @param params 顧客情報更新に必要なパラメータ
 * @returns 更新された顧客情報
 */
export const putCustomer = async (
  id: number,
  params: UpdateCustomer,
): Promise<Result<OriginalCustomer, Error>> => {
  const url = constructUrl(`${URL}/${id}`);

  return axios
    .put(url, {
      ...params,
    })
    .then((res) => {
      const data = res.data as unknown;
      if (!isOriginalCustomer(data)) {
        throw Error(`Unsupported Value: ${JSON.stringify(data)}`);
      }

      return createOk(data);
    })
    .catch((err: Error) => createErr(err));
};

/**
 * 顧客を削除する
 * @param id 削除する顧客の ID
 * @returns null
 */
export const deleteCustomer = async (
  id: number,
): Promise<Result<null, Error>> => {
  const url = constructUrl(`${URL}/${id}`);

  return axios
    .delete(url)
    .then(() => createOk(null))
    .catch((err: Error) => createErr(err));
};

/**
 * 顧客に紐づく契約一覧を取得する
 * @param id 顧客 ID
 * @returns 顧客
 */
export const fetchContracts = async (
  id: number,
): Promise<Result<OriginalContract[], Error>> => {
  const url = constructUrl(`${URL}/${id}/contracts`);

  return axios
    .get(url)
    .then((res) => {
      const data = res.data as unknown;
      if (!isOriginalContracts(data)) {
        throw Error(`Unsupported Value: ${JSON.stringify(data)}`);
      }

      return createOk(data);
    })
    .catch((err: Error) => createErr(err));
};

/**
 * 契約情報を作成する
 * @param customerId 顧客 ID
 * @param params 契約情報作成に必要なパラメータ
 * @returns 作成された契約情報
 */
// TODO: 変数名 id を customerId に変更
export const postContract = async (
  customerId: number,
  params: NewContract,
): Promise<Result<null, Error>> => {
  const url = constructUrl(`${URL}/${customerId}/contracts`);

  return axios
    .post(url, {
      ...params,
    })
    .then(() => createOk(null))
    .catch((err: Error) => createErr(err));
};

/**
 * 契約情報を更新する
 * @param customerId 顧客 ID
 * @param contractId 契約 ID
 * @param params 契約情報作成に必要なパラメータ
 * @returns 更新された契約情報
 */
export const putContract = async (
  customerId: number,
  contractId: number,
  params: UpdateContract,
): Promise<Result<null, Error>> => {
  const url = constructUrl(`${URL}/${customerId}/contracts/${contractId}`);

  return axios
    .put(url, {
      ...params,
    })
    .then(() => createOk(null))
    .catch((err: Error) => createErr(err));
};

/**
 * 契約情報を削除する
 * @param customerId 顧客 ID
 * @param contractId 契約 ID
 * @returns
 */
export const deleteContract = async (
  customerId: number,
  contractId: number,
): Promise<Result<null, Error>> => {
  const url = constructUrl(`${URL}/${customerId}/contracts/${contractId}`);

  return axios
    .delete(url)
    .then(() => createOk(null))
    .catch((err: Error) => createErr(err));
};
